import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Generate and download a PDF from HTML element
 * @param {HTMLElement} element - The HTML element to convert
 * @param {string} filename - The filename for the PDF (without .pdf extension)
 */
export const downloadPDFFromElement = async (element, filename) => {
  try {
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const imgWidth = 210; // A4 width in mm
    const pageHeight = 295; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    // Add image to PDF, creating new pages if necessary
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`${filename}.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

/**
 * Generate professional invoice PDF using jsPDF
 * @param {Object} invoice - Invoice data object
 * @param {string} customerName - Customer name
 * @param {Object} userCompany - Company/user info object
 * @param {string} userCompany.company - Company name
 * @param {string} userCompany.email - Company email
 * @param {string} userCompany.phone - Company phone
 * @param {string} userCompany.address - Company address
 * @param {string} userCompany.city - Company city
 * @param {string} userCompany.state - Company state
 * @param {string} userCompany.zipCode - Company zip code
 * @param {string} userCompany.logo - Logo as base64 or URL (optional)
 */
export const generateInvoicePDF = async (invoice, customerName, userCompany = {}) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let yPosition = 10;

  // Set default font
  pdf.setFont('Helvetica');

  // ===== HEADER SECTION =====
  // Logo - Top Center
  if (userCompany.logo) {
    try {
      const logoSize = 30; // 30mm x 30mm
      const logoX = (pageWidth - logoSize) / 2;
      pdf.addImage(userCompany.logo, 'PNG', logoX, yPosition, logoSize, logoSize);
      yPosition += logoSize + 5;
    } catch (err) {
      console.error('Error adding logo:', err);
      yPosition += 5;
    }
  } else {
    yPosition += 5;
  }

  // Company info on LEFT and Invoice details on RIGHT
  const headerLeftX = 15;
  const headerRightX = pageWidth - 80;
  let headerYPos = yPosition;

  // Left side - Company Details
  pdf.setFontSize(11);
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('Helvetica', 'bold');
  if (userCompany.company) {
    pdf.text(userCompany.company, headerLeftX, headerYPos);
    headerYPos += 6;
  }

  pdf.setFont('Helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(100, 100, 100);

  if (userCompany.address) {
    pdf.text(userCompany.address, headerLeftX, headerYPos);
    headerYPos += 5;
  }

  let cityLine = '';
  if (userCompany.city) cityLine += userCompany.city;
  if (userCompany.state) cityLine += (cityLine ? ', ' : '') + userCompany.state;
  if (userCompany.zipCode) cityLine += ' ' + userCompany.zipCode;
  if (cityLine) {
    pdf.text(cityLine, headerLeftX, headerYPos);
    headerYPos += 5;
  }

  if (userCompany.phone) {
    pdf.text(`Phone: ${userCompany.phone}`, headerLeftX, headerYPos);
    headerYPos += 5;
  }

  if (userCompany.email) {
    pdf.text(`Email: ${userCompany.email}`, headerLeftX, headerYPos);
  }

  // Right side - Invoice Details
  headerYPos = yPosition;
  pdf.setFontSize(16);
  pdf.setTextColor(25, 118, 210);
  pdf.setFont('Helvetica', 'bold');
  pdf.text('INVOICE', headerRightX + 15, headerYPos);
  headerYPos += 10;

  pdf.setFont('Helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);

  pdf.setFont('Helvetica', 'bold');
  pdf.text('Invoice #:', headerRightX, headerYPos);
  pdf.setFont('Helvetica', 'normal');
  pdf.text(`${invoice.invoice_number || `INV-${invoice.id}`}`, headerRightX + 30, headerYPos);
  headerYPos += 6;

  pdf.setFont('Helvetica', 'bold');
  pdf.text('Date:', headerRightX, headerYPos);
  pdf.setFont('Helvetica', 'normal');
  pdf.text(new Date(invoice.created_at || new Date()).toLocaleDateString(), headerRightX + 30, headerYPos);
  headerYPos += 6;

  pdf.setFont('Helvetica', 'bold');
  pdf.text('Due Date:', headerRightX, headerYPos);
  pdf.setFont('Helvetica', 'normal');
  pdf.text(new Date(invoice.due_date).toLocaleDateString(), headerRightX + 30, headerYPos);
  headerYPos += 6;

  pdf.setFont('Helvetica', 'bold');
  pdf.text('Status:', headerRightX, headerYPos);
  pdf.setFont('Helvetica', 'normal');
  const statusText = invoice.status || 'draft';
  pdf.text(statusText.charAt(0).toUpperCase() + statusText.slice(1), headerRightX + 30, headerYPos);

  // Separator line
  yPosition += 50;
  pdf.setDrawColor(50, 118, 210);
  pdf.setLineWidth(0.5);
  pdf.line(15, yPosition, pageWidth - 15, yPosition);
  yPosition += 8;

  // ===== BILL TO SECTION =====
  pdf.setFontSize(11);
  pdf.setTextColor(25, 118, 210);
  pdf.setFont('Helvetica', 'bold');
  pdf.text('BILL TO:', 15, yPosition);
  yPosition += 7;

  pdf.setFontSize(10);
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('Helvetica', 'normal');
  const splitCustomerName = pdf.splitTextToSize(customerName, 60);
  pdf.text(splitCustomerName, 15, yPosition);
  yPosition += splitCustomerName.length * 5 + 3;

  // Separator line
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.3);
  pdf.line(15, yPosition, pageWidth - 15, yPosition);
  yPosition += 8;

  // ===== ITEMS/DETAILS TABLE =====
  // Table Header
  pdf.setFontSize(10);
  pdf.setTextColor(255, 255, 255);
  pdf.setFillColor(25, 118, 210);
  pdf.setFont('Helvetica', 'bold');

  const tableStartX = 15;
  const descriptionColX = tableStartX;
  const amountColX = pageWidth - 40;

  // Header background
  pdf.rect(tableStartX, yPosition - 5, pageWidth - 30, 7, 'F');
  pdf.text('Description', descriptionColX + 3, yPosition);
  pdf.text('Amount', amountColX + 5, yPosition);
  yPosition += 8;

  // Item details
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('Helvetica', 'normal');
  pdf.setFontSize(9);

  const maxDescWidth = 100;
  const splitDesc = pdf.splitTextToSize(invoice.description || 'Service', maxDescWidth);
  pdf.text(splitDesc, descriptionColX + 3, yPosition);

  const descHeight = splitDesc.length * 5;
  const amountY = yPosition + (descHeight - 5);
  pdf.text(`$${parseFloat(invoice.amount).toFixed(2)}`, amountColX + 15, amountY);

  yPosition += descHeight + 5;

  // ===== SUMMARY SECTION =====
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.3);
  pdf.line(15, yPosition, pageWidth - 15, yPosition);
  yPosition += 7;

  const totalLabelX = pageWidth - 90;
  const totalValueX = pageWidth - 40;

  // Total row
  pdf.setFont('Helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(25, 118, 210);
  pdf.text('TOTAL:', totalLabelX, yPosition);
  pdf.text(`$${parseFloat(invoice.amount).toFixed(2)}`, totalValueX, yPosition);

  // ===== FOOTER =====
  yPosition = pageHeight - 20;
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.3);
  pdf.line(15, yPosition, pageWidth - 15, yPosition);
  yPosition += 8;

  pdf.setFont('Helvetica', 'italic');
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text('Thank you for your business!', pageWidth / 2 - 22, yPosition);

  // Save the PDF
  const filename = `Invoice-${invoice.invoice_number || invoice.id}`;
  pdf.save(`${filename}.pdf`);
};
